/**
 * Eski ayar sekmelerinin (Gizlilik, Bildirimler, Görünüm, Dil) CİHAZLAR ARASI anlık eşitlemesi.
 *
 * Bu dört sekme `/auth/me/*` uçlarını tek-atış okuyup yerel `useState`'te tutuyor. API her
 * kayıtta kullanıcının DİĞER oturumlarına `USER_SETTINGS_UPDATE` yolluyor ve değişen sekmenin
 * görünümünü `tebhos_legacy.<sekme>` altında taşıyor — ama web bunu hiç dinlemiyordu: başka
 * cihazda değişen tema/dil/bildirim ayarı bu cihazda ancak sayfa yenilenince görünüyordu.
 *
 * İki iş yapar:
 *  1. Yan etkiler (tema, dil, bildirim aynası, hassas medya önbelleği) ayarlar penceresi
 *     KAPALIYKEN de uygulanır — kaydın yapıldığı cihazdaki sekmenin yaptığıyla aynı.
 *  2. Sekme açıksa `useLegacySettingsRemote` ile yerel durumunu yeniler.
 *
 * Yankı yok: API olayı kaydı yapan oturuma da yollar; görünüm sekmenin az önce kaydettiği
 * değerle aynı olduğundan yeniden uygulamak zararsızdır (idempotent).
 */
import { useEffect, useRef } from "react";
import type { AppearanceSettings } from "@app/features/user/components/modals/user_settings/tabs/AppearanceTab/_api";
import type { LanguageSettings } from "@app/features/user/components/modals/user_settings/tabs/LanguageTab/_api";
import type { NotificationPreferences } from "@app/features/user/components/modals/user_settings/tabs/NotificationsTab/_api";
import type { PrivacySettings } from "@app/features/user/components/modals/user_settings/tabs/PrivacySafetyTab/_api";
import { tebhosWs } from "@/lib/api/ws-client";

export interface LegacySettingsViews {
  privacy: PrivacySettings;
  notifications: NotificationPreferences;
  appearance: AppearanceSettings;
  language: LanguageSettings;
}
export type LegacySettingsBlob = keyof LegacySettingsViews;

const BLOBS: ReadonlyArray<LegacySettingsBlob> = ["privacy", "notifications", "appearance", "language"];

type Listener<K extends LegacySettingsBlob> = (view: LegacySettingsViews[K]) => void;
const listeners: { [K in LegacySettingsBlob]: Set<Listener<K>> } = {
  privacy: new Set(),
  notifications: new Set(),
  appearance: new Set(),
  language: new Set(),
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Olay yükünden `tebhos_legacy` altındaki sekme görünümlerini ayıklar (saf). */
export function extractLegacyViews(payload: unknown): Partial<LegacySettingsViews> {
  if (!isRecord(payload) || !isRecord(payload.tebhos_legacy)) return {};
  const out: Partial<Record<LegacySettingsBlob, unknown>> = {};
  for (const blob of BLOBS) {
    const view = payload.tebhos_legacy[blob];
    if (isRecord(view)) out[blob] = view;
  }
  return out as Partial<LegacySettingsViews>;
}

// Yan etki modülleri TEMBEL yüklenir: bu dosya uygulama girişinden (index.tsx) kurulur; ayar
// sekmesi modüllerini girişte statik içe aktarmak modül döngüsü (başlatılmadan erişim) ve
// dil etkinleşmeden çeviri çağrısı üretiyordu (tarayıcı testinde yakalandı).
async function applySideEffects(views: Partial<LegacySettingsViews>): Promise<void> {
  if (views.appearance) {
    const a = views.appearance;
    const theme = await import("@app/features/user/components/modals/user_settings/tabs/AppearanceTab/_themeManager");
    theme.applyTheme(a.theme, a.accentColor, a.gradientTheme, a.customGradient);
    theme.cacheAppearance(a.theme, a.accentColor, a.gradientTheme, a.customGradient);
  }
  if (views.language?.locale) {
    const i18n = await import("@app/app/I18n");
    i18n.applyLocaleChange(views.language.locale);
  }
  if (views.notifications?.notifications) {
    const mirror = await import("@app/features/notification/state/NotifPrefsMirror");
    mirror.NotifPrefsMirrorStore.setFromServer(views.notifications.notifications);
  }
  // Hassas medya düzeyi gizlilik görünümünden türetilip önbelleklenir → bayatlamasın.
  if (views.privacy) {
    const sensitive = await import("@app/features/mature_content/hooks/useSensitiveMedia");
    sensitive.invalidateSensitiveCache();
  }
}

/** Tek olayı işler: yan etkiler (tembel) + açık sekmelerin dinleyicileri. Yan etkiler bitince çözülür. */
export function handleLegacySettingsEvent(payload: unknown): Promise<void> {
  const views = extractLegacyViews(payload);
  const effects = applySideEffects(views).catch((error) => {
    console.error("[LegacySettingsLiveSync] yan etki uygulanamadı", error);
  });
  for (const blob of BLOBS) {
    const view = views[blob];
    if (!view) continue;
    for (const listener of listeners[blob] as Set<Listener<typeof blob>>) listener(view as never);
  }
  return effects;
}

let installed: (() => void) | null = null;

/** Uygulama ömrü boyunca BİR kez kurulur (tekrar çağrı etkisizdir); kaldırma işlevi döner. */
export function installLegacySettingsLiveSync(): () => void {
  if (installed) return installed;
  const off = tebhosWs.on("USER_SETTINGS_UPDATE", (data: unknown) => void handleLegacySettingsEvent(data));
  installed = () => {
    off();
    installed = null;
  };
  return installed;
}

/** Açık sekme: başka cihazdan gelen görünümle yerel durumu yenile. */
export function useLegacySettingsRemote<K extends LegacySettingsBlob>(blob: K, onRemote: Listener<K>): void {
  // Geri çağrı her çizimde değişebilir; abonelik sabit kalsın.
  const latest = useRef(onRemote);
  latest.current = onRemote;
  useEffect(() => {
    const listener: Listener<K> = (view) => latest.current(view);
    const set = listeners[blob] as Set<Listener<K>>;
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }, [blob]);
}
